import React, { useState } from 'react';
import { Box, Typography, Popover, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatCurrency } from '../collectPayment.helper';

export interface ConsumePackageBannerProps {
  /** Amount consumed from package (Advance Deducted) */
  consumeAmount: number;
  /** Currency code for formatting */
  currency: string;
  /** Total bill amount */
  totalBill?: number;
  /** Pending amount after advance deduction */
  pendingAmount?: number;
  /** Available balance in the package (from validate API) */
  packageBalance?: number;
  /** Name of the package being consumed */
  packageName?: string;
  /** Custom label for the banner (default: "Consume package advance amount :") */
  bannerLabel?: string;
  /** Custom label for Total Bill in popover (default: "Total Bill") */
  totalBillLabel?: string;
  /** Custom label for Advance Deducted in popover (default: "Advance Deducted") */
  advanceDeductedLabel?: string;
  /** Custom label for Pending Amount in popover (default: "Pending Amount") */
  pendingAmountLabel?: string;
}

/**
 * ConsumePackageBanner - Displays package consumption info with info icon
 * 
 * Shows a banner indicating how much is being consumed from package advance.
 * Clicking the info icon shows a popover with detailed breakdown.
 */
const ConsumePackageBanner: React.FC<ConsumePackageBannerProps> = ({
  consumeAmount,
  currency,
  totalBill,
  pendingAmount,
  packageBalance,
  packageName,
  bannerLabel = 'Consume package advance amount :',
  totalBillLabel = 'Total Bill',
  advanceDeductedLabel = 'Advance Deducted',
  pendingAmountLabel = 'Pending Amount',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'consume-package-popover' : undefined;

  if (consumeAmount <= 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#FFF9E6',
          border: '1px solid #FFE082',
          borderRadius: '8px',
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: '#5D4037' }}>
          {bannerLabel}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 600, 
              color: '#5D4037' 
            }}
          >
            {formatCurrency(consumeAmount, currency)}
          </Typography>
          <IconButton
            aria-describedby={id}
            onClick={handleInfoClick}
            size="small"
            sx={{
              p: 0.5,
              color: '#F5A623',
              '&:hover': {
                color: '#E09515',
                backgroundColor: 'rgba(245, 166, 35, 0.1)',
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Info Popover */}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              minWidth: 280,
              maxWidth: 360,
            },
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 220 }}>
          {/* Package Name */}
          {packageName && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#333' }}>
                Package Name
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                {packageName}
              </Typography>
            </Box>
          )}

          {/* Package Balance */}
          {packageBalance != null && packageBalance > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#333' }}>
                Package Balance
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                {formatCurrency(packageBalance, currency)}
              </Typography>
            </Box>
          )}

          {/* Total Bill */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#333' }}>
              {totalBillLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {formatCurrency(totalBill ?? 0, currency)}
            </Typography>
          </Box>

          {/* Advance Deducted */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#333' }}>
              {advanceDeductedLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {formatCurrency(consumeAmount, currency)}
            </Typography>
          </Box>

          {/* Pending Amount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: '#333' }}>
              {pendingAmountLabel}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {formatCurrency(pendingAmount ?? 0, currency)}
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ConsumePackageBanner;
